import React,{FC, useEffect, useState} from "react";
import Modal from "react-bootstrap/Modal";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { UserPayoutAccount } from "../../../../models/userPayoutAccounts";
import { 
    CLUB_ACTIVE_STATUS, 
    CLUB_INACTIVE_STATUS, 
    INTEGOLF_BANK_ACCOUNT_INFO_TYPE_BANK_TRANSFER, 
    INTEGOLF_BANK_ACCOUNT_INFO_TYPE_BANK_INTERACT_ID 
} from "../../../../utils/constants";
import { trimString } from "../../../../utils/common";
import { useTranslation } from "react-i18next";

interface IPayoutAccountDetails {
    accountNumber : string;
    confirmAccountNumber : string;
    accountHolderName ?: string; 
    bicSwiftCode ?: string;
    status ?: number;
    maskedAccountNumber ?: string;
    interactId ?: string;
    type?: string;
}

interface IAddPayoutAccountDetailsPopUp {
    showModal: boolean;
    payoutAccountInfo ?: UserPayoutAccount;
    onCloseClick : () => void;
    onSubmitClick: (arg: any) => void;
}

const AddPayoutAccountDetailsPopUp : FC<IAddPayoutAccountDetailsPopUp> = ({
    showModal = false,
    payoutAccountInfo,
    onCloseClick,
    onSubmitClick
}) => {

    const { 
        register, 
        unregister,
        handleSubmit, 
        formState: { errors, dirtyFields }, 
        reset, 
        getValues, 
        setError, 
        clearErrors, 
        setValue,
        control: payoutAccountInfoController 
    } = useForm<IPayoutAccountDetails>();

    const [payoutAccountType,setPayoutAccountType]=useState<string|undefined>("")
    const [payoutAccountInfoPresent,setPayoutAccountInfoPresent]=useState<Boolean>(false);
    const payoutAccountInfoType = getValues('type');

    const confirmAccountNumberValidationStep = (isValid: boolean) => {
        if (isValid) {
            clearErrors("confirmAccountNumber");
        } else {
            setError('confirmAccountNumber', {type: 'manual'});
        }
    }

    const validateConfirmAccountNumber = () : boolean => {
        let confirmAccNumberisValid = true;
        if (getValues("confirmAccountNumber") && getValues('accountNumber')) {
            if(getValues("confirmAccountNumber") !== getValues('accountNumber')){
                confirmAccNumberisValid = false;
            }
        }
        return confirmAccNumberisValid;
    }
    

    const onConfirmAccNumberChange = () => {
        confirmAccountNumberValidationStep(validateConfirmAccountNumber());
    }

    const onSubmit: SubmitHandler<IPayoutAccountDetails> = (data: any) => {

        // validate confirm account number on re render manual errors gets cleared automatically
        if (validateConfirmAccountNumber()) {
            confirmAccountNumberValidationStep(true);
            if (payoutAccountInfo && payoutAccountInfo._id) {
                const { accountHolderName, bicSwiftCode, status, type, interactId } = data;
                
                const updates: any = {
                    ...(dirtyFields["accountHolderName"] ? {accountHolderName}: {}),
                    ...(dirtyFields["bicSwiftCode"] ? { bicSwiftCode }: {}),
                    ...(dirtyFields["status"]? {status}: {}),
                    ...(dirtyFields["type"] ? { type }: {}),
                    ...(dirtyFields["interactId"] ? { interactId }: {})
                };
    
                if (Object.keys(updates).length) {
                    updates["id"] = payoutAccountInfo._id;
                    onSubmitClick(updates);
                }
    
            } else {
                onSubmitClick(data);
                reset();
            }
        } else {
            confirmAccountNumberValidationStep(false);
        }

    }


    useEffect(()=> {
        if (payoutAccountInfo && payoutAccountInfo._id!=="") {
            //making the true if info is present.
            setPayoutAccountInfoPresent(true);
        }else{
            setPayoutAccountInfoPresent(false);
            reset();
        }
    }, [payoutAccountInfo]);

    useEffect(() => {
        if (!showModal) {
            reset({ type: "" });
        }
    }, [showModal]);

    useEffect(()=>{
        if (payoutAccountInfo && payoutAccountInfo._id!==""&& payoutAccountInfoPresent===true) {
            // set pop up values
            setValue("accountHolderName",payoutAccountInfo.accountHolderName);
            setValue("maskedAccountNumber", payoutAccountInfo.maskedAccountNumber);
            setValue("bicSwiftCode", payoutAccountInfo.bicSwiftCode);
            setValue("status", payoutAccountInfo?.status);
            setValue("interactId", payoutAccountInfo?.interactId);
            setValue("type", payoutAccountInfo?.type)
            setPayoutAccountType(payoutAccountInfo?.type)
            // validation rule still persists after the field is hidden so explicitly unregistering the field
            unregister("accountNumber");
            unregister("confirmAccountNumber")
        }
    },[payoutAccountInfo,payoutAccountInfoPresent]);

    const {t}=useTranslation("")

    return <Modal
        className="modalLayoutWrapper"
        show={showModal}
        backdrop="static"
        size="xl"
        keyboard={false}
        centered
    >
        <Modal.Body>
            <div className="container">
                <a href={void 0} className="closeButton" onClick={() => onCloseClick()}>
                    <em className="fa-solid fa-close"></em>
                </a>
                <div className="row justify-content-center">
                    <h4 className="mb-4 text-center">{t("PayoutPageDetailsForm.title")}</h4>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="gutters-10 pb-4">
                            <div className="form-floating">
                                <Controller
                                    control={payoutAccountInfoController}
                                    name="type"
                                    render={({field}) => <>
                                        <select
                                            className="form-select border rounded-4 border-dark fw700 fontsize-12 my-2"
                                            id="type"
                                            disabled={!!(payoutAccountInfo && payoutAccountInfo?._id)}
                                            {...field}
                                            {...register("type", {required: true})}
                                            onSelect={(e: any) =>{
                                                 field.onChange(e?.target?.value);
                                                 setPayoutAccountType(e?.target?.value)
                                            }}
                                            onChange={(e: any) =>{
                                                const value = e?.target?.value;
                                                field.onChange(value);
                                                setPayoutAccountType(value);
                                                reset({type:value})
                                            }}
                                        >
                                            <option disabled selected value="">{t("PayoutPageDetailsForm.formFields.formField1.selectionText")}</option>
                                            <option value={INTEGOLF_BANK_ACCOUNT_INFO_TYPE_BANK_TRANSFER}>{t("PayoutPageDetailsForm.formFields.formField1.BankTransfer")}</option>
                                            <option value={INTEGOLF_BANK_ACCOUNT_INFO_TYPE_BANK_INTERACT_ID}>{t("PayoutPageDetailsForm.formFields.formField1.InteracID")}</option>
                                        </select>
                                    </>}
                                />
                                <label htmlFor="type">{t("PayoutPageDetailsForm.formFields.formField1.label")}</label>
                                {errors.type?.type == 'required' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField1.requiredError")}</div>}
                            </div>
                        </div>

                        {
                            (payoutAccountType ? payoutAccountType== INTEGOLF_BANK_ACCOUNT_INFO_TYPE_BANK_TRANSFER : payoutAccountInfoType == INTEGOLF_BANK_ACCOUNT_INFO_TYPE_BANK_TRANSFER) ? <>
                                {
                                    (payoutAccountInfo && payoutAccountInfo._id) ? <div className="gutters-10 pb-4">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                id="maskaccnumber"
                                                disabled={true}
                                                placeholder={t("PayoutPageDetailsForm.formFields.formField2.placeholder")}
                                                className="form-control"
                                                {...register("maskedAccountNumber")}
                                            />
                                            <label htmlFor="maskaccnumber">{t("PayoutPageDetailsForm.formFields.formField2.label")}</label>
                                        </div>
                                    </div> : <div className="gutters-10 pb-4">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                id="accnumber"
                                                placeholder={t("PayoutPageDetailsForm.formFields.formField3.placeholder")}
                                                className="form-control"
                                                {...register("accountNumber", { required: true, pattern: /^\d{7,12}$/, onChange: onConfirmAccNumberChange })}
                                            />
                                            <label htmlFor="accnumber">{t("PayoutPageDetailsForm.formFields.formField3.label")}</label>
                                        </div>
                                        {errors.accountNumber?.type == 'required' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField3.requiredError")}</div>}
                                        {errors.accountNumber?.type == 'pattern' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField3.validationError")}</div>}
                                    </div>
                                }    
                                {
                                    !(payoutAccountInfo && payoutAccountInfo._id) && <div className="gutters-10 pb-4">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                id="confirmaccnumber"
                                                placeholder={t("PayoutPageDetailsForm.formFields.formField4.placeholder")}
                                                className="form-control"
                                                {...register("confirmAccountNumber", { required: true, pattern: /^\d{7,12}$/, onChange: validateConfirmAccountNumber})}
                                            />
                                            <label htmlFor="confirmaccnumber">{t("PayoutPageDetailsForm.formFields.formField4.label")}</label>
                                        </div>
                                        {errors.confirmAccountNumber?.type == 'required' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField4.requiredError")}</div>}
                                        {errors.confirmAccountNumber?.type == 'pattern' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField4.patternError")}</div>}
                                        {errors.confirmAccountNumber?.type == 'manual' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField4.manualError")}</div>}
                                    </div> 
                                }
                                <div className="gutters-10 pb-4">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            placeholder={t("PayoutPageDetailsForm.formFields.formField5.placeholder")}
                                            className="form-control"
                                            id="accountHolderName"
                                            {...register("accountHolderName", { 
                                                required: true,
                                                pattern: /^[a-zA-Z\s\-']{2,50}$/,
                                                setValueAs: trimString 
                                            })}
                                        />
                                        <label htmlFor="accountHolderName">{t("PayoutPageDetailsForm.formFields.formField5.label")}</label>
                                    </div>
                                    {errors.accountHolderName?.type == 'required' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField5.requiredError")}</div>}
                                    {errors.accountHolderName?.type == 'pattern' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField5.validationError")}</div>}
                                </div>
                                <div className="gutters-10 pb-4">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            placeholder={t("PayoutPageDetailsForm.formFields.formField6.placeholder")}
                                            className="form-control"
                                            id="bicSwiftCode"
                                            {...register("bicSwiftCode", { 
                                                required: true,
                                                pattern: /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
                                                setValueAs: trimString 
                                            })}
                                        />
                                        <label htmlFor="bicSwiftCode">{t("PayoutPageDetailsForm.formFields.formField6.label")}</label>
                                    </div>
                                    {errors.bicSwiftCode?.type == "required"&& <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField6.requiredError")}</div>}
                                    {errors.bicSwiftCode?.type == 'pattern' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField6.validationError")}</div>}
                                </div>
                            </> : <div/> 
                        }
                        {
                            (payoutAccountType ? payoutAccountType== INTEGOLF_BANK_ACCOUNT_INFO_TYPE_BANK_INTERACT_ID : payoutAccountInfoType == INTEGOLF_BANK_ACCOUNT_INFO_TYPE_BANK_INTERACT_ID) ? <div className="gutters-10 pb-4">
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        placeholder={t("PayoutPageDetailsForm.formFields.formField7.placeholder")}
                                        className="form-control"
                                        id="interactId"
                                        {...register("interactId", { 
                                            required: true, 
                                            pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                            setValueAs: trimString  
                                        })}
                                    />
                                    <label htmlFor="interactId">{t("PayoutPageDetailsForm.formFields.formField7.label")}</label>
                                    {errors.interactId?.type == 'required' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField7.requiredError")}</div>}
                                    {errors.interactId?.type == 'pattern' && <div className="isInvalidMessage text-danger">{t("PayoutPageDetailsForm.formFields.formField7.patternError")}</div>}
                                </div>
                            </div> : <div/>
                        }
                        {
                            (payoutAccountInfo?.status) && <div className="gutters-10 pb-4">
                                <div className="form-floating">
                                    <Controller
                                        control={payoutAccountInfoController}
                                        name="status"
                                        render={({field}) => <>
                                            <select
                                                className="form-select border rounded-4 border-dark fw700 fontsize-12 my-2"
                                                id="status"
                                                disabled={payoutAccountInfo.status == CLUB_ACTIVE_STATUS}
                                                {...field}
                                                onSelect={(e: any) => field.onChange(parseInt(e?.target?.value+''))}
                                            >
                                                <option value={CLUB_ACTIVE_STATUS+''}>{t("PayoutPageDetailsForm.formFields.formField8.Active")}</option>
                                                <option value={CLUB_INACTIVE_STATUS+''}>{t("PayoutPageDetailsForm.formFields.formField8.InActive")}</option>
                                            </select>
                                        </>}
                                    />
                                    <label htmlFor="status">{t("PayoutPageDetailsForm.formFields.formField8.label")}</label>
                                </div>
                            </div>
                        }
                        <div className="d-grid gap-2">
                            <button
                                type="submit"
                                className="button button-primary button-rounded button-lg fw700"
                            >
                                <span>{t("PayoutPageDetailsForm.Save")}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div> 
        </Modal.Body>
    </Modal>;
}

export default AddPayoutAccountDetailsPopUp;